/**
 * Oznaczenia lokali do WYŚWIETLANIA (E3, raport uwag Bronka 2026-10-19; migr. 071): drzwi (`displayLabel`) i klatka
 * (`staircase`). Wolności 532: na drzwiach 13–24 (numeracja krocząca po klatkach), w CEEB w każdej klatce 1–10.
 *
 * ⚠️ Numer lokalu (`apartmentNumber`) zostaje tożsamością w CEEB (bot, import, dopasowanie) — tu go nie zmieniamy.
 * Zapis: PUT /api/apartments/labels (apartmentsApi.updateLabels). Czyste funkcje — apartmentLabels.test.ts.
 * Lustra tekstu: backend/src/utils/apartmentLabel.js, mobile/src/utils/apartmentNumbers.ts (`apartmentDoorMarking`).
 */

export const DISPLAY_LABEL_MAX = 50; // VARCHAR(50)
export const STAIRCASE_MAX = 20; // VARCHAR(20)
/** Najwięcej lokali w jednym zakresie (jak „Dodaj lokale od–do" na tablecie). */
export const MAX_LABEL_RANGE = 300;

export interface ApartmentLabelParts {
  displayLabel?: string | null;
  staircase?: string | null;
}

const clean = (v?: string | null) => (typeof v === 'string' ? v.trim() : '');

/** „kl. II, drzwi 13" / „drzwi 22/27b" / „kl. II" / '' — bez numeru CEEB (ten stoi obok). */
export function apartmentDoorMarking(parts: ApartmentLabelParts | null | undefined): string {
  const door = clean(parts?.displayLabel);
  const kl = clean(parts?.staircase);
  return [kl ? `kl. ${kl}` : '', door ? `drzwi ${door}` : ''].filter(Boolean).join(', ');
}

/**
 * „13 (kl. II, drzwi 1)" / „13" — numer CEEB pierwszy, oznaczenie z panelu w nawiasie. Dla list, które idą
 * do zarządcy (zna lokale po numerach na drzwiach). Lustro: mobile/src/utils/unavailableApartments.ts
 * (`unavailableApartmentLabel`).
 */
export function apartmentNumberWithMarking(apartmentNumber: string, parts: ApartmentLabelParts | null | undefined): string {
  const num = String(apartmentNumber ?? '').trim();
  const marking = apartmentDoorMarking(parts);
  return marking ? `${num} (${marking})` : num;
}

/** Liczba całkowita z numeru lokalu („13", „ 13 ") albo null („13a", „22/27b", „Żabka"). */
export function plainApartmentNumber(raw: string | null | undefined): number | null {
  const s = String(raw ?? '').trim();
  return /^\d{1,5}$/.test(s) ? Number(s) : null;
}

export interface LabelRangeInput {
  /** Numery CEEB „od–do" (włącznie). */
  from: number;
  to: number;
  /** Klatka dla całego zakresu; '' = bez zmiany klatki. */
  staircase: string;
  /** Pierwszy numer na drzwiach (dla lokalu `from`); null = bez zmiany drzwi. */
  doorFrom: number | null;
}

export interface LabelRangeRow {
  apartmentId: string;
  apartmentNumber: string;
  displayLabel?: string;
  staircase?: string;
}

export type LabelRangePlan =
  | { error: string }
  | { rows: LabelRangeRow[]; skipped: string[] };

/**
 * „Klatka od–do": lokale o numerach CEEB `from`…`to` → klatka i kolejne numery drzwi. Drzwi liczone z PRZESUNIĘCIA
 * numeru (lokal `from` = `doorFrom`, lokal `from + k` = `doorFrom + k`), więc brakujący lokal w CEEB nie przesuwa
 * numeracji reszty. Lokale z nietypowym numerem („13a", „22/27b") w zakresie nie wchodzą — `skipped` (do edycji
 * pojedynczo). Pole niepodane (klatka '' / drzwi null) nie trafia do zapisu, więc zostaje bez zmian.
 */
export function planLabelRange(
  apartments: readonly { id: string; apartmentNumber: string }[],
  input: LabelRangeInput,
): LabelRangePlan {
  const { from, to, doorFrom } = input;
  const staircase = clean(input.staircase);
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < 0) return { error: 'Podaj numery lokali „od" i „do" (liczby całkowite).' };
  if (to < from) return { error: '„Do" nie może być mniejsze niż „od".' };
  if (to - from + 1 > MAX_LABEL_RANGE) return { error: `Najwięcej ${MAX_LABEL_RANGE} lokali naraz.` };
  if (doorFrom !== null && (!Number.isInteger(doorFrom) || doorFrom < 0)) return { error: 'Pierwszy numer na drzwiach musi być liczbą całkowitą.' };
  if (!staircase && doorFrom === null) return { error: 'Podaj klatkę albo pierwszy numer na drzwiach.' };
  if (staircase.length > STAIRCASE_MAX) return { error: `Klatka: najwyżej ${STAIRCASE_MAX} znaków.` };
  const rows: LabelRangeRow[] = [];
  const skipped: string[] = [];
  for (const a of apartments) {
    const n = plainApartmentNumber(a.apartmentNumber);
    if (n === null) {
      // Nietypowy numer zaczynający się od liczby z zakresu („13a") — pokaż, że został pominięty.
      const lead = /^\s*(\d+)/.exec(a.apartmentNumber || '');
      if (lead && Number(lead[1]) >= from && Number(lead[1]) <= to) skipped.push(a.apartmentNumber.trim());
      continue;
    }
    if (n < from || n > to) continue;
    rows.push({
      apartmentId: a.id,
      apartmentNumber: a.apartmentNumber.trim(),
      ...(doorFrom !== null ? { displayLabel: String(doorFrom + (n - from)) } : {}),
      ...(staircase ? { staircase } : {}),
    });
  }
  rows.sort((x, y) => Number(x.apartmentNumber) - Number(y.apartmentNumber));
  return { rows, skipped };
}
